import { useEffect, useMemo, useState } from 'react';
import { Link, Select, Skeleton, Text } from '@backstage/ui';
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { useApi } from '@backstage/core-plugin-api';
import { useRouteRef } from '@backstage/frontend-plugin-api';
import { liteLlmApiRef } from '../api';
import { rootRouteRef } from '../routes';
import { UsageMetrics } from '../types';
import { fmtUsd } from '../format';
import { toLocalDay } from '../dates';
import { useLiteLLMProfile } from '../hooks/useLiteLLMProfile';
import {
  widgetViewState,
  USAGE_UNAVAILABLE_MSG,
  UNPROVISIONED_MSG,
} from '../widgetState';
import {
  rangeCaption,
  sparklineAriaLabel,
  usageSummary,
} from '../homeWidgetHelpers';
import { ChartTooltip, SERIES } from './ui';
import styles from './LiteLLMHomeWidget.module.css';

export interface LiteLLMHomeWidgetProps {
  /** Default period when the widget mounts. Defaults to '7d'. */
  defaultPeriod?: 'today' | '7d' | '30d';
  /** Optional title override. Defaults to 'LiteLLM Usage'. */
  title?: string;
  /**
   * Draw the surrounding card and heading. Turn it off inside a host that
   * already supplies them, such as the Backstage home grid, whose
   * `HomePageWidgetBlueprint` renders its own card — two frames otherwise.
   */
  frame?: boolean;
}

type DatePreset = 'today' | '7d' | '30d';

const PERIOD_OPTIONS = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
];

function presetToDateRange(preset: DatePreset): { start: Date; end: Date } {
  const end = new Date();
  const start = new Date();
  if (preset === 'today') {
    start.setHours(0, 0, 0, 0);
  } else if (preset === '7d') {
    start.setDate(start.getDate() - 7);
  } else {
    start.setDate(start.getDate() - 30);
  }
  return { start, end };
}

export const LiteLLMHomeWidget = ({
  defaultPeriod = '7d',
  title = 'LiteLLM Usage',
  frame = true,
}: LiteLLMHomeWidgetProps) => {
  const api = useApi(liteLlmApiRef);
  // Undefined when the plugin's page is not installed; the link is then left out.
  const routeLink = useRouteRef(rootRouteRef);
  const {
    userInfo,
    keys,
    loading: profileLoading,
    error: profileError,
  } = useLiteLLMProfile();
  const [period, setPeriod] = useState<DatePreset>(defaultPeriod);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState<unknown>(null);
  const [usage, setUsage] = useState<UsageMetrics | null>(null);

  const { startDate, endDate } = useMemo(() => {
    const { start, end } = presetToDateRange(period);
    return { startDate: toLocalDay(start), endDate: toLocalDay(end) };
  }, [period]);

  useEffect(() => {
    let cancelled = false;
    setUsageLoading(true);
    setUsageError(null);
    api
      .getUsage(startDate, endDate)
      .then(result => {
        if (cancelled) return;
        setUsage(result);
        setUsageLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setUsageError(err);
        setUsage(null);
        setUsageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, startDate, endDate]);

  const viewState = useMemo(
    () =>
      widgetViewState({
        loading: profileLoading || usageLoading,
        error: profileError,
        userInfo,
        usageError,
        hasKeys: keys.length > 0,
      }),
    [profileLoading, usageLoading, profileError, userInfo, usageError, keys],
  );

  const dailyData = (usage?.daily_usage ?? []).map(d => ({
    date: d.date,
    spend: d.spend,
  }));
  const showSparkline =
    viewState.kind === 'ready' &&
    !viewState.usageUnavailable &&
    dailyData.length > 0;

  const renderBody = () => {
    switch (viewState.kind) {
      case 'loading':
        return (
          <div className={styles.loading}>
            <Skeleton width="60%" height={16} />
            <Skeleton width="100%" height={80} />
          </div>
        );
      // Quiet, generic copy: raw backend errors never reach a widget.
      case 'error':
        return (
          <Text variant="body-small" className={styles.quiet}>
            {viewState.message}
          </Text>
        );
      case 'unprovisioned':
        return (
          <Text variant="body-small" className={styles.quiet}>
            {UNPROVISIONED_MSG}
          </Text>
        );
      default:
        return (
          <>
            <Text
              variant="body-small"
              className={
                viewState.usageUnavailable ? styles.quiet : styles.summary
              }
            >
              {viewState.usageUnavailable
                ? USAGE_UNAVAILABLE_MSG
                : usageSummary(usage)}
            </Text>

            {showSparkline && (
              <>
                <div
                  className={styles.spark}
                  role="img"
                  aria-label={sparklineAriaLabel(dailyData)}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={dailyData}
                      margin={{ top: 4, right: 0, bottom: 0, left: 0 }}
                    >
                      <XAxis dataKey="date" hide />
                      <Tooltip
                        content={<ChartTooltip valueFormatter={fmtUsd} />}
                      />
                      <Area
                        type="monotone"
                        dataKey="spend"
                        name="Spend"
                        stroke={SERIES.spend}
                        fill={SERIES.spend}
                        fillOpacity={0.3}
                        dot={false}
                        isAnimationActive={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <Text variant="body-x-small" className={styles.caption}>
                  daily spend ({rangeCaption(startDate, endDate)})
                </Text>
              </>
            )}

            {routeLink && (
              <div className={styles.footer}>
                <Link href={routeLink()} className={styles.footerLink}>
                  Open LiteLLM →
                </Link>
              </div>
            )}
          </>
        );
    }
  };

  const body = (
    <>
      {/* Unframed, the heading comes from the host, so only the period control
          remains — pushed right so it lines up with the framed layout. */}
      <div className={frame ? styles.head : styles.headBare}>
        {frame && <Text variant="title-x-small">{title}</Text>}
        <Select
          aria-label="Usage period"
          className={styles.period}
          options={PERIOD_OPTIONS}
          selectedKey={period}
          onSelectionChange={key => setPeriod(key as DatePreset)}
        />
      </div>
      {renderBody()}
    </>
  );

  return frame ? <div className={styles.card}>{body}</div> : body;
};
